// src/modules/myvault/components/automations/actions/RunSkillAction.tsx

// What? RUN_SKILL editor: pick a MyVault agent skill + fill its JSON args.
// Why?  The backend dispatcher takes `{skill_id, ...}`; skill-specific typed
//       forms would be a big yak-shave per skill, so v1 accepts a JSON blob
//       with a small "Available parameters" hint from the skill metadata.

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Option,
  Select,
  Stack,
  Textarea,
} from "@mui/joy";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import type { ActionEditorProps } from "../types";

interface SkillInfo {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

const RunSkillAction: React.FC<ActionEditorProps> = ({ config, onChange }) => {
  const authFetch = useAuthFetch();
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await authFetch(
          "/api/workspace/myvault/skills",
          "fetch skills",
        );
        if (!cancelled && Array.isArray(data)) setSkills(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load skills",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const skillId = typeof config.skill_id === "string" ? config.skill_id : "";
  const argsJson = (() => {
    if (typeof config.args === "string") return config.args;
    if (config.args && typeof config.args === "object") {
      try {
        return JSON.stringify(config.args, null, 2);
      } catch {
        return "{}";
      }
    }
    return "{}";
  })();

  const [jsonText, setJsonText] = useState(argsJson);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync local buffer when an outside change swaps the action to a different
  // skill (rare but possible when reordering action cards).
  useEffect(() => {
    setJsonText(argsJson);
  }, [argsJson]);

  const handleJsonBlur = () => {
    try {
      const parsed = jsonText.trim() === "" ? {} : JSON.parse(jsonText);
      onChange({ args: parsed });
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : "Invalid JSON");
    }
  };

  const selected = skills.find((s) => s.name === skillId);

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Skill</FormLabel>
        <Select
          size="sm"
          value={skillId}
          onChange={(_, val) => onChange({ skill_id: (val as string) || "" })}
          placeholder="Select a skill"
        >
          <Option value="">None</Option>
          {skills.map((s) => (
            <Option key={s.name} value={s.name}>
              {s.name}
            </Option>
          ))}
        </Select>
        {error && (
          <FormHelperText sx={{ color: "danger.600" }}>{error}</FormHelperText>
        )}
        {selected?.description && (
          <FormHelperText>{selected.description}</FormHelperText>
        )}
      </FormControl>

      {selected && selected.parameters && (
        <Alert size="sm" color="neutral" variant="soft">
          <Box
            component="pre"
            sx={{ m: 0, fontSize: "11px", maxHeight: 180, overflow: "auto" }}
          >
            {JSON.stringify(selected.parameters, null, 2)}
          </Box>
        </Alert>
      )}

      <FormControl>
        <FormLabel>Arguments (JSON)</FormLabel>
        <Textarea
          size="sm"
          minRows={4}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          onBlur={handleJsonBlur}
          sx={{ fontFamily: "monospace", fontSize: "12px" }}
        />
        {jsonError ? (
          <FormHelperText sx={{ color: "danger.600" }}>
            {jsonError}
          </FormHelperText>
        ) : (
          <FormHelperText>
            Pass the arguments the skill expects. Typed per-skill forms are a v2
            improvement.
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
};

export default RunSkillAction;
